import { Badge, Button, Card, Divider, Input, Select, Table } from '@scuf/common';
import { deliveryTimeOptions, listAproved, reasonRejection } from 'models/spareParts';
import { OrderDTO, OrderItemsDTO, OrderHistoryDTO } from 'models/spareParts/orderById';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { orderAtom } from 'resources/atoms/spareParts';
import Budget from './budget';
import { processOrder } from 'resources/api/spareParts_api/processOrder';
import { MAINTENANCES_REPORTS, SPARE_PARTS, notificationAtom } from 'resources';
import useUser from 'helpers/hooks/useUser';
import { UserRole } from 'models/user/sso';
import useFetch from 'helpers/hooks/useFetch';
import { getHistory } from 'resources/api/spareParts_api/getHistory';
import { typeOpt } from 'models/spareParts';
import { formatDate } from 'helpers';
import { get } from 'http';
import { Report, ReportItem } from 'models/maintenanceReport/allMaintenances';

const Price: React.FC = () => {
    const [orderHistory, setOrderHistory] = useState<OrderHistoryDTO[]>([]);
    const [filteredHistory, setShowHistory] = useState<OrderHistoryDTO[]>([]);
    const [orderData, setorderData] = useRecoilState<OrderDTO>(orderAtom);
    const [orderItems, setOrderItems] = useState<OrderItemsDTO[]>();
    const { t } = useTranslation(['spareparts', 'common']);
    const [loading, setLoading] = useState<boolean>(false);
    const [disable, setDisable] = useState<boolean>(false);
    const setNotification = useSetRecoilState(notificationAtom);
    const [customerApprovals, setCustomerApprovals] = useState<Record<number, boolean | null>>({});
    const [customReason, setCustomReason] = useState<Record<number, boolean>>({});
    const [loadingHistory, setLoadingHistory] = useState<boolean>(false);

    const [report, getReport, loagingGetReport] = useFetch<{ id: number }, Report>({
        url: MAINTENANCES_REPORTS.getReportById,
        method: 'GET'
    });

    const statusRenderer = (status: number) => {
        const statusMap: any = {
            1: { color: 'yellow', content: t('common:status.WaitingApproval') },
            2: { color: 'red', content: t('common:status.Disapproved') },
            3: { color: 'green', content: t('common:status.Approved') },
            4: { color: 'yellow', content: t('common:status.PartiallyApproved') },
            5: { color: 'grey', content: t('common:status.Finished') }
        };

        const defaultStatus = { color: 'gray', content: t('common:status.NotInformed') };
        const currentStatus = statusMap[status] || defaultStatus;

        return <Badge color={currentStatus.color} content={currentStatus.content} />;
    };

    useEffect(() => {
        if (!orderData?.id || !orderData?.orderItems?.length) {
            setOrderHistory([]);
            setShowHistory([]);
            return;
        }

        getOrderHistory();
    }, [orderData?.id]);

    useEffect(() => {
        if (Number(orderData?.typeId) !== 5) {
            return;
        }

        const id = Number(orderData?.number);
        const companyId = Number(orderData?.companyId);

        if (!Number.isFinite(id) || !Number.isFinite(companyId)) {
            return;
        }

        getReport({
            id
        });
    }, [orderData?.id, orderData?.typeId, orderData?.number, orderData?.companyId]);

    useEffect(() => {
        setOrderItems(orderData?.orderItems ?? []);
    }, [orderData?.id, orderData?.orderItems]);

    const getOrderHistory = async () => {
        try {
            setLoadingHistory(true);

            const serials = Array.from(
                new Set(
                    (orderData?.orderItems ?? [])
                        .map(item => item.serialNumberEquipment)
                        .filter((serial): serial is string => serial !== undefined && serial !== null && serial.trim() !== '')
                )
            );

            if (serials.length === 0) {
                setOrderHistory([]);
                setShowHistory([]);
                return;
            }

            const responses = await Promise.all(serials.map(serial => getHistory(serial)));

            const allHistory = responses.flatMap(response => {
                if (response.status !== 200) {
                    return [];
                }

                return response.data as OrderHistoryDTO[];
            });

            const currentOrderDate = new Date(orderData.createdAt).getTime();

            const historyBeforeCurrentOrder = allHistory.filter(historyItem => {
                const historyDate = new Date(historyItem.orderDate).getTime();

                return historyItem.orderId !== orderData.id && historyDate < currentOrderDate;
            });

            const uniqueHistory = Array.from(
                new Map(
                    historyBeforeCurrentOrder.map(historyItem => [
                        [historyItem.orderId, historyItem.partNumber, historyItem.serialNumberPart].join('-'),
                        historyItem
                    ])
                ).values()
            );

            setOrderHistory(allHistory);
            setShowHistory(uniqueHistory);
        } catch (error) {
            console.error('Erro ao carregar histórico:', error);

            setOrderHistory([]);
            setShowHistory([]);

            setNotification(old => ({
                ...old,
                message: 'Não foi possível carregar o histórico do equipamento'
            }));
        } finally {
            setLoadingHistory(false);
        }
    };

    const { user } = useUser();

    const process = async () => {
        setLoading(true);
        setDisable(true);
        const aux = { ...orderData, orderItems: [...orderItems!], roleId: parseInt(user?.role!) };

        const resp = await processOrder(aux);
        if (resp.error) {
            setNotification(old => ({ ...old, message: 'Erro ao Processar Pedido' }));
        } else {
            setorderData(resp.data);
            setOrderHistory([]);
            setNotification(old => ({ ...old, message: 'Pedido Processado', type: 'success' }));
        }
        setLoading(false);
    };

    const SUPERADMIN = '1';
    const ADMIN = '2';
    const admin = user?.role === SUPERADMIN || user?.role === ADMIN;

    const warranty = () => {
        let type = typeOpt.find(e => e.value === orderData?.typeId);
        if (type?.value === 4) return true;
        return false;
    };

    const contract = () => {
        let type = typeOpt.find(e => e.value === orderData?.typeId);
        if (type?.value === 5) return true;
        return false;
    };

    const isVerifyClientCondition1 = () => {
        return orderItems?.every(
            item =>
                item.adminApproval !== undefined &&
                (item.adminApproval === true || item.adminApproval === false) &&
                item.customerApproval === null
        );
    };

    const isVerifyClientCondition2 = () => {
        return orderItems?.every(item => {
            const customerApproved = item.customerApproval;
            const reasonRejected = item.reasonRejection;
            const isValid =
                customerApproved !== undefined &&
                ((customerApproved === false &&
                    reasonRejected &&
                    reasonRejected.trim() !== '' &&
                    reasonRejected.trim() !== 'Outros motivos') ||
                    customerApproved === true);

            return isValid;
        });
    };

    const isVerifyClientCondition3 = () => {
        const result = orderItems?.every(
            item => item.customerApproval === true && (item.adminApproval === true || item.adminApproval === false)
        );
        return result;
    };

    const isButtonEnabled = () => {
        if (orderData?.typeId === 2) {
            if (orderData?.statusId === 2) {
                return isVerifyClientCondition1();
            } else if (orderData?.statusId === 3) {
                return isVerifyClientCondition2();
            }
        } else if (orderData?.typeId === 3 || orderData?.typeId === 4 || orderData?.typeId === 5) {
            return isVerifyClientCondition3();
        }

        return false;
    };

    const showResultBadge = (orderItems?: OrderItemsDTO[], itemId?: number) => {
        let label = '';
        let color: any = 'yellow';
        const admin = orderItems?.find(e => e.id === itemId && e.adminApproval !== undefined)?.adminApproval ?? null;
        const customer = orderItems?.find(e => e.id === itemId && e.customerApproval !== undefined)?.customerApproval ?? null;

        if (customer === null && admin === null) {
            label = 'common:status.Processing';
        } else if (customer === true && admin === null) {
            label = 'common:status.ApprovedByCustomer';
            color = 'green';
        } else if (customer === false && (admin || admin === null)) {
            label = 'common:status.RejectedByCustomer';
            color = 'red';
        } else if ((customer || customer === null) && admin === false) {
            label = 'common:status.RejectedByCompany';
            color = 'red';
        } else if ((customer && admin) || (admin && (orderData.typeId == 3 || orderData.typeId == 4 || orderData.typeId == 5))) {
            label = 'common:status.Approved';
            color = 'green';
        } else if (customer === false && admin === false) {
            label = 'common:status.Rejected';
            color = 'red';
        } else if (admin === true || customer === null) {
            label = 'common:status.ApprovedByCompany';
            color = 'green';
        }
        return <Badge color={color} content={t(label)} />;
    };

    const isContract = Number(orderData?.typeId) === 5;
    const isLoadingContractData = isContract && (loadingHistory || loagingGetReport);

    return (
        <>
            <div className="container-selects">
                <Budget></Budget>
                <div className="div-datatable">
                    <div>
                        <h3>{t('spareparts:translation.OrderItems') + '(' + orderData?.orderItems?.length + ')'}</h3>
                        <Divider />
                        <Table>
                            <Table.Header>
                                <Table.HeaderCell content={t('common:translation.Item')} />
                                {orderData?.typeId != 4 && orderData?.typeId != 5 ? (
                                    <Table.HeaderCell content={t('spareparts:translation.Deadline')} />
                                ) : (
                                    <Table.HeaderCell content={t('spareparts:productinfots.order_data.SerialNumberPart')} />
                                )}
                                {orderData?.typeId != 4 && orderData?.typeId != 5 ? (
                                    <Table.HeaderCell content={t('spareparts:translation.DeadlinePersonlized')} />
                                ) : (
                                    <Table.HeaderCell content={t('spareparts:productinfots.order_data.SerialNumberEqp')} />
                                )}
                                <Table.HeaderCell content={t('spareparts:translation.HoneywellAprroval')} />
                                <Table.HeaderCell content={t('spareparts:translation.CustomerAprroval')} />
                                <Table.HeaderCell content={t('common:translation.Result')} />
                            </Table.Header>
                            <Table.Body>
                                {orderData?.orderItems?.map((item: OrderItemsDTO, index: number) => (
                                    <Table.Row key={index}>
                                        <Table.Cell width={3}>
                                            {item?.partNumber} {' / '}
                                            {item?.description}
                                        </Table.Cell>

                                        {orderData?.typeId != 4 && orderData?.typeId != 5 ? (
                                            <Table.Cell width={3}>
                                                <Select
                                                    className="select-type"
                                                    disabled={
                                                        [UserRole.CLIENT, UserRole.CLIENTADMIN].includes(user?.role!) ||
                                                        orderData?.statusId === 3 ||
                                                        orderData?.statusId !== 2
                                                    }
                                                    options={deliveryTimeOptions}
                                                    placeholder={item?.deadline}
                                                    onChange={value => {
                                                        setOrderItems(old =>
                                                            [...old!].map(e => {
                                                                if (e.id == item.id) return { ...e, deadline: value };
                                                                else return e;
                                                            })
                                                        );
                                                    }}
                                                />
                                            </Table.Cell>
                                        ) : (
                                            <Table.Cell width={3}>{item?.serialNumberPart}</Table.Cell>
                                        )}

                                        <Table.Cell width={3}>
                                            {orderData?.typeId != 4 && orderData?.typeId != 5 ? (
                                                <Input
                                                    disabled={
                                                        [UserRole.CLIENT, UserRole.CLIENTADMIN].includes(user?.role!) ||
                                                        orderData?.statusId === 3 ||
                                                        orderData?.statusId !== 2
                                                    }
                                                    onChange={value => {
                                                        setOrderItems(old =>
                                                            [...old!].map(e => {
                                                                if (e.id == item.id) return { ...e, deadline: value };
                                                                else return e;
                                                            })
                                                        );
                                                        setDisable(true);
                                                    }}
                                                    placeholder={item?.deadline}
                                                    // value={item?.deadline}
                                                />
                                            ) : (
                                                item?.serialNumberEquipment
                                            )}
                                        </Table.Cell>

                                        <Table.Cell width={3}>
                                            <Select
                                                className="select-type"
                                                options={listAproved}
                                                disabled={
                                                    [UserRole.CLIENT, UserRole.CLIENTADMIN].includes(user?.role!) ||
                                                    orderData?.statusId === 3 ||
                                                    orderData?.statusId !== 2
                                                }
                                                placeholder={
                                                    item?.adminApproval === true
                                                        ? t('common:status.Approved')
                                                        : item?.adminApproval === false
                                                          ? t('common:status.Rejected')
                                                          : t('common:status.WaitingCompany')
                                                }
                                                onChange={value => {
                                                    setOrderItems(old =>
                                                        [...old!].map(e => {
                                                            if (
                                                                e.id == item.id &&
                                                                (orderData.typeId === 4 || orderData.typeId === 5) &&
                                                                value
                                                            )
                                                                return { ...e, adminApproval: value, customerApproval: true };
                                                            else if (e.id == item.id) return { ...e, adminApproval: value };
                                                            else return e;
                                                        })
                                                    );
                                                    setDisable(true);
                                                }}
                                            />
                                        </Table.Cell>

                                        <Table.Cell width={3}>
                                            <Select
                                                disabled={
                                                    [UserRole.ADMIN, UserRole.SUPERADMIN].includes(user?.role!) ||
                                                    item?.customerApproval ||
                                                    orderData?.statusId === 2
                                                }
                                                className="select-type"
                                                placeholder={
                                                    orderData?.typeId === 3 ||
                                                    orderData?.typeId === 4 ||
                                                    orderData?.typeId === 5 ||
                                                    item.customerApproval === true
                                                        ? t('common:status.Approved')
                                                        : item?.customerApproval === false
                                                          ? t('common:status.Rejected')
                                                          : t('common:status.WaitingClient')
                                                }
                                                onChange={value => {
                                                    setOrderItems(old =>
                                                        old!.map(e =>
                                                            e.id === item.id
                                                                ? {
                                                                      ...e,
                                                                      customerApproval: value,
                                                                      reasonRejection: value === true ? '' : e.reasonRejection
                                                                  }
                                                                : e
                                                        )
                                                    );
                                                    setCustomerApprovals(prev => ({ ...prev, [item.id]: value }));

                                                    setDisable(true);
                                                }}
                                                options={listAproved}
                                            />

                                            {orderData?.typeId == 2 && customerApprovals[item.id] !== true && (
                                                <Table.Cell width={3} className="Select-Reason">
                                                    <Select
                                                        disabled={
                                                            [UserRole.ADMIN, UserRole.SUPERADMIN].includes(user?.role!) ||
                                                            orderData?.statusId !== 3 ||
                                                            customerApprovals[item.id] !== false
                                                        }
                                                        className="select-type"
                                                        placeholder={
                                                            item.reasonRejection
                                                                ? item.reasonRejection
                                                                : t('common:placeholders.Select.SelectReason')
                                                        }
                                                        options={reasonRejection}
                                                        onChange={value => {
                                                            setOrderItems(old =>
                                                                [...old!].map(e => {
                                                                    if (e.id == item.id) return { ...e, reasonRejection: value };
                                                                    else return e;
                                                                })
                                                            );
                                                            setDisable(true);

                                                            if (value === 'Outros motivos') {
                                                                setCustomReason(prev => ({ ...prev, [item.id]: true }));
                                                            } else {
                                                                setCustomReason(prev => {
                                                                    const newReasons = { ...prev };
                                                                    delete newReasons[item.id];
                                                                    return newReasons;
                                                                });
                                                            }
                                                        }}
                                                    />

                                                    {orderData?.statusId === 3 && customReason[item.id] && (
                                                        <Table.Cell className="OtherReasons">
                                                            <Input
                                                                disabled={!customReason[item.id]}
                                                                onChange={value => {
                                                                    setOrderItems(old =>
                                                                        [...old!].map(e => {
                                                                            if (e.id == item.id)
                                                                                return { ...e, reasonRejection: value };
                                                                            else return e;
                                                                        })
                                                                    );
                                                                    setDisable(true);
                                                                }}
                                                                placeholder={
                                                                    item.reasonRejection
                                                                        ? item.reasonRejection
                                                                        : t('spareparts:translation.TellReason')
                                                                }
                                                            />
                                                        </Table.Cell>
                                                    )}
                                                </Table.Cell>
                                            )}
                                        </Table.Cell>

                                        <Table.Cell>{showResultBadge(orderItems, item.id)}</Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table>
                        <Card.Content className="buttonsSearch">
                            <Button
                                loading={loading}
                                type="primary"
                                disabled={!isButtonEnabled() || loading}
                                icon="slidercontrols-plus"
                                onClick={() => process()}
                                content={t('spareparts:productinfots.order_data.ProcessOrder')}
                                iconPosition="right"
                            />
                        </Card.Content>
                        <div>
                            {admin && warranty() && filteredHistory.length > 0 && (
                                <div>
                                    <h3>Histórico</h3>
                                    <Divider />
                                    <Table>
                                        <Table.Header>
                                            <Table.HeaderCell content={t('common:translation.Item')} />
                                            <Table.HeaderCell
                                                content={t('spareparts:productinfots.order_data.SerialNumberPart')}
                                            />
                                            <Table.HeaderCell
                                                content={t('spareparts:productinfots.order_data.SerialNumberEqp')}
                                            />
                                            <Table.HeaderCell content={t('spareparts:translation.DateOrder')} />
                                            <Table.HeaderCell content={t('spareparts:translation.OrderId')} />
                                            <Table.HeaderCell content={t('common:translation.Partner')} />
                                        </Table.Header>
                                        <Table.Body>
                                            {filteredHistory.map((item: OrderHistoryDTO, index: number) => (
                                                <Table.Row key={index}>
                                                    <Table.Cell width={3}>
                                                        {item?.partNumber}
                                                        {' / '}
                                                        {item?.description}
                                                    </Table.Cell>
                                                    <Table.Cell width={3}>{item?.serialNumberPart}</Table.Cell>
                                                    <Table.Cell width={3}>{item?.serialNumberEquipment}</Table.Cell>
                                                    <Table.Cell width={3}>{formatDate(item?.orderDate)}</Table.Cell>
                                                    <Table.Cell width={3}>{item?.orderId}</Table.Cell>
                                                    <Table.Cell width={3}>{item?.orderCompanyName}</Table.Cell>
                                                </Table.Row>
                                            ))}
                                        </Table.Body>
                                    </Table>
                                </div>
                            )}{' '}
                            {contract() && admin && !isLoadingContractData && report?.data != null && (
                                <div>
                                    <h3>Histórico</h3>
                                    <Divider />
                                    {filteredHistory.length > 0 ? (
                                        <Table>
                                            <Table.Header>
                                                <Table.HeaderCell content={t('common:translation.Item')} />
                                                <Table.HeaderCell
                                                    content={t('spareparts:productinfots.order_data.SerialNumberPart')}
                                                />
                                                <Table.HeaderCell
                                                    content={t('spareparts:productinfots.order_data.SerialNumberEqp')}
                                                />
                                                <Table.HeaderCell
                                                    content={t('spareparts:productinfots.order_data.DateCreation')}
                                                />
                                                <Table.HeaderCell content={t('spareparts:productinfots.order_data.OrderId')} />
                                                <Table.HeaderCell content={t('common:translation.Partner')} />
                                            </Table.Header>
                                            <Table.Body>
                                                {filteredHistory.map((item: OrderHistoryDTO, index: number) => (
                                                    <Table.Row key={index}>
                                                        <Table.Cell width={3}>
                                                            {item?.partNumber}
                                                            {' / '}
                                                            {item?.description}
                                                        </Table.Cell>
                                                        <Table.Cell width={3}>{item?.serialNumberPart}</Table.Cell>
                                                        <Table.Cell width={3}>{item?.serialNumberEquipment}</Table.Cell>
                                                        <Table.Cell width={3}>{formatDate(item?.orderDate)}</Table.Cell>
                                                        <Table.Cell width={3}>{item?.orderId}</Table.Cell>
                                                        <Table.Cell width={3}>{item?.orderCompanyName}</Table.Cell>
                                                    </Table.Row>
                                                ))}
                                            </Table.Body>
                                        </Table>
                                    ) : (
                                        <div style={{ padding: '16px 0', color: '#666' }}>
                                            Não existe histórico para esse equipamento
                                        </div>
                                    )}
                                    <h3>Dados do Relatório de Manutenção</h3>
                                    <Divider />
                                    <Table>
                                        <Table.Header>
                                            <Table.HeaderCell content={t('spareparts:translation.MaintenanceReportId')} />
                                            <Table.HeaderCell content={t('common:translation.DateCreate')} />
                                            <Table.HeaderCell content={t('common:translation.Partner')} />
                                            <Table.HeaderCell content={t('common:translation.SerialNumber')} />
                                            <Table.HeaderCell content={t('common:translation.Product')} />
                                            <Table.HeaderCell content={t('common:translation.Status')} />
                                        </Table.Header>
                                        <Table.Body>
                                            <Table.Row>
                                                <Table.Cell width={3}>{report.data.Id}</Table.Cell>
                                                <Table.Cell width={3}>{formatDate(report.data.CreatedAt)}</Table.Cell>
                                                <Table.Cell width={3}>{report.data.CompanyName}</Table.Cell>
                                                <Table.Cell width={3}>{report.data.SerialNumber}</Table.Cell>
                                                <Table.Cell width={3}>{report.data.ProductName}</Table.Cell>
                                                <Table.Cell width={3}>{statusRenderer(report.data.ApprovalStatusId)}</Table.Cell>
                                            </Table.Row>
                                        </Table.Body>
                                    </Table>
                                    <h3>Itens Utilizados no reparo</h3>
                                    <Divider />
                                    {(report.data.ReportItems ?? []).length > 0 ? (
                                        <Table>
                                            <Table.Header>
                                                <Table.HeaderCell content={t('common:translation.PartNumber')} />
                                                <Table.HeaderCell content={t('common:translation.Description')} />
                                                <Table.HeaderCell content={t('common:translation.Date')} />
                                            </Table.Header>
                                            <Table.Body>
                                                {report.data.ReportItems.map((item: ReportItem, index: number) => (
                                                    <Table.Row key={index}>
                                                        <Table.Cell width={2}>{item.PartNumber}</Table.Cell>
                                                        <Table.Cell width={4}>{item.Description}</Table.Cell>
                                                        <Table.Cell width={1}>{formatDate(item?.CreatedAt)}</Table.Cell>
                                                    </Table.Row>
                                                ))}
                                            </Table.Body>
                                        </Table>
                                    ) : (
                                        <div style={{ padding: '16px 0', color: '#666' }}>Nenhum item utilizado no reparo</div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <br></br>
            </div>
        </>
    );
};
export default Price;
